import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  SampleMasterFormModal,
  ServiceMasterViewModal,
  ServiceMasterDeleteModal,
  MultiSelectDropdown,
  ModuleHeader
} from 'components/modals';
import type {
  SampleMasterFormData,
  ActionButton,
  ViewModeOption
} from 'components/modals';

/**
 * ============================================================================
 * SERVICE MASTER SCREEN v2.0 - PRODUCTION READY
 * ============================================================================
 * 
 * NEW FEATURES IN v2.0:
 * âœ… Column Visibility Control (with default/optional columns)
 * âœ… Grid View with service cards
 * âœ… Calendar View (disabled/inactive)
 * âœ… Multiple BU/Entity selection in filters
 * âœ… Searchable Service Category dropdown
 * âœ… Searchable UOM dropdown
 * âœ… Similar service name suggestions
 * âœ… Enhanced validation rules (Entity→Enterprise, single tax at entity)
 * âœ… All previous features maintained
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceMasterRecord {
  id: string;
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
  createdAt: string;
  createdBy: string;
}

interface TaxDetail {
  id: string;
  taxType: string;
  taxCode: string;
  taxRate: string;
  jurisdiction: string;
}

interface ServiceCategory {
  id: string;
  name: string;
  defaultEntities: string[];
  defaultGLAccount: string;
  defaultTaxes: TaxDetail[];
}

interface GLAccount {
  id: string;
  code: string;
  name: string;
  category: string;
}

interface Entity {
  id: string;
  code: string;
  name: string;
  type: 'Entity' | 'Business Unit';
}

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

type ViewMode = 'list' | 'grid' | 'calendar';

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleCategories: ServiceCategory[] = [
  {
    id: 'cat1',
    name: 'Audit & Assurance Services',
    defaultEntities: ['entity1', 'bu1'],
    defaultGLAccount: 'gl1',
    defaultTaxes: [
      { id: 'tax1', taxType: 'GST', taxCode: 'HSN9978', taxRate: '18%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat2',
    name: 'Tax Advisory Services',
    defaultEntities: ['entity1', 'entity2'],
    defaultGLAccount: 'gl2',
    defaultTaxes: [
      { id: 'tax2', taxType: 'GST', taxCode: 'HSN9983', taxRate: '18%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat3',
    name: 'Financial Advisory Services',
    defaultEntities: ['entity1'],
    defaultGLAccount: 'gl3',
    defaultTaxes: [
      { id: 'tax3', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
    ]
  }
];

const sampleEntities: Entity[] = [
  { id: 'entity1', code: 'E001', name: 'Sample Entity A', type: 'Entity' },
  { id: 'entity2', code: 'E002', name: 'Sample Entity B', type: 'Entity' },
  { id: 'entity3', code: 'E003', name: 'Test Entity 1', type: 'Entity' },
  { id: 'bu1', code: 'BU001', name: 'Sample Business Unit 1', type: 'Business Unit' },
  { id: 'bu2', code: 'BU002', name: 'Sample Business Unit 2', type: 'Business Unit' },
  { id: 'bu3', code: 'BU003', name: 'Test Business Unit 1', type: 'Business Unit' }
];

const sampleGLAccounts: GLAccount[] = [
  { id: 'gl1', code: '4001', name: 'Audit Revenue', category: 'Revenue' },
  { id: 'gl2', code: '4002', name: 'Tax Advisory Revenue', category: 'Revenue' },
  { id: 'gl3', code: '4003', name: 'Financial Advisory Revenue', category: 'Revenue' },
  { id: 'gl4', code: '4004', name: 'Consulting Revenue', category: 'Revenue' },
  { id: 'gl5', code: '4005', name: 'Other Professional Revenue', category: 'Revenue' }
];

const sampleUOMs: string[] = ['Hour', 'Day', 'Week', 'Month', 'Job', 'Project', 'Deliverable'];

const sampleTaxServices: TaxDetail[] = [
  { id: 'tax1', taxType: 'GST', taxCode: 'HSN9978', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax2', taxType: 'GST', taxCode: 'HSN9983', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax3', taxType: 'GST', taxCode: 'HSN9985', taxRate: '12%', jurisdiction: 'India' },
  { id: 'tax4', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
];

const sampleServices: ServiceMasterRecord[] = [
  {
    id: '1',
    code: 'SVC001',
    serviceName: 'Statutory Audit',
    description: 'Annual statutory audit services as per Companies Act',
    category: 'Audit & Assurance Services',
    entities: ['entity1', 'bu1'],
    uom: 'Job',
    effectiveFrom: '2024-01-01',
    effectiveTo: '',
    status: 'Active',
    glAccount: 'gl1',
    taxDetails: [sampleTaxServices[0]],
    scope: 'Entity',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  },
  {
    id: '2',
    code: 'SVC002',
    serviceName: 'Internal Audit',
    description: 'Internal audit and control assessment services',
    category: 'Audit & Assurance Services',
    entities: ['entity1', 'entity2'],
    uom: 'Month',
    effectiveFrom: '2024-01-01',
    effectiveTo: '',
    status: 'Active',
    glAccount: 'gl1',
    taxDetails: [sampleTaxServices[0]],
    scope: 'Enterprise',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  },
  {
    id: '3',
    code: 'SVC003',
    serviceName: 'Tax Compliance Services',
    description: 'GST, Income Tax, and TDS compliance services',
    category: 'Tax Advisory Services',
    entities: ['entity1', 'entity2', 'bu2'],
    uom: 'Month',
    effectiveFrom: '2024-01-01',
    effectiveTo: '',
    status: 'Active',
    glAccount: 'gl2',
    taxDetails: [sampleTaxServices[1]],
    scope: 'Entity',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  },
  {
    id: '4',
    code: 'SVC004',
    serviceName: 'GST Advisory',
    description: 'GST planning, optimization and advisory services',
    category: 'Tax Advisory Services',
    entities: ['entity1'],
    uom: 'Hour',
    effectiveFrom: '2024-01-01',
    effectiveTo: '',
    status: 'Active',
    glAccount: 'gl2',
    taxDetails: [sampleTaxServices[1]],
    scope: 'BU',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  },
  {
    id: '5',
    code: 'SVC005',
    serviceName: 'Financial Due Diligence',
    description: 'Financial due diligence for M&A transactions',
    category: 'Financial Advisory Services',
    entities: ['entity1', 'bu3'],
    uom: 'Project',
    effectiveFrom: '2024-01-01',
    effectiveTo: '',
    status: 'Active',
    glAccount: 'gl3',
    taxDetails: [sampleTaxServices[2]],
    scope: 'Entity',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  },
  {
    id: '6',
    code: 'SVC006',
    serviceName: 'Valuation Services',
    description: 'Business valuation and intangible asset valuation',
    category: 'Financial Advisory Services',
    entities: ['entity1'],
    uom: 'Project',
    effectiveFrom: '2024-01-01',
    effectiveTo: '2024-12-31',
    status: 'Inactive',
    glAccount: 'gl3',
    taxDetails: [sampleTaxServices[2]],
    scope: 'BU',
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  }
];

// ============================================================================
// COLUMN DEFINITIONS (v2.0 - NEW)
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  {
    id: 'select',
    label: '',
    field: 'select',
    required: true,
    defaultVisible: true,
    width: 50,
    sortable: false
  },
  {
    id: 'code',
    label: 'Code',
    field: 'code',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'serviceName',
    label: 'Service Name',
    field: 'serviceName',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'description',
    label: 'Description',
    field: 'description',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: false
  },
  {
    id: 'category',
    label: 'Category',
    field: 'category',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'uom',
    label: 'UOM',
    field: 'uom',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'status',
    label: 'Status',
    field: 'status',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'entities',
    label: 'Entities',
    field: 'entities',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: false
  },
  {
    id: 'effectiveFrom',
    label: 'Effective From',
    field: 'effectiveFrom',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'effectiveTo',
    label: 'Effective To',
    field: 'effectiveTo',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'glAccount',
    label: 'GL Account',
    field: 'glAccount',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'actions',
    label: 'Actions',
    field: 'actions',
    required: true,
    defaultVisible: true,
    width: 80,
    sortable: false
  }
];

// ============================================================================
// COLUMN SELECTOR COMPONENT (v2.0 - NEW)
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;

    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns
      .filter(col => !col.required)
      .map(col => col.id);
    
    const requiredIds = columns
      .filter(col => col.required)
      .map(col => col.id);

    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to default column configuration?')) {
      onReset();
      setIsOpen(false);
    }
  };

  const handleApply = () => {
    onApply(selectedColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedColumns(initialVisible);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? {
          background: 'var(--accent-gradient)',
          color: 'white',
          borderColor: 'var(--primary)'
        } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          {isMobile && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setIsOpen(false)}
            />
          )}

          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          {/* Header */}
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                width: '1.5rem',
                height: '1.5rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.25rem'
              }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 0',
            minHeight: 0
          }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!column.required) {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.paddingLeft = '1.5rem';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? {
                    background: 'var(--accent-gradient)',
                    borderColor: 'var(--primary)',
                    color: 'white'
                  } : {})
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{
                  flex: 1,
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                  userSelect: 'none'
                }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleReset}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="btn btn-primary"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </>
      )}
    </div>
  );
};

// ============================================================================
// GRID VIEW CARD COMPONENT (v2.0 - NEW)
// ============================================================================

interface ServiceCardProps {
  service: ServiceMasterRecord;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, onView, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const glAccount = sampleGLAccounts.find(gl => gl.id === service.glAccount);
  const primaryTax = service.taxDetails[0];

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: '0.25rem'
          }}>
            {service.code}
          </div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            {service.serviceName}
          </h3>
        </div>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className={`action-menu-trigger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ marginTop: '-0.25rem' }}
          >
            <IconSystem.moreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="action-menu-dropdown" style={{ right: 0, top: 'calc(100% + 0.5rem)' }}>
              <button className="action-menu-item" onClick={() => { onView(service.id); setMenuOpen(false); }}>
                <IconSystem.eye size={18} />
                <span>View Details</span>
              </button>
              <button className="action-menu-item" onClick={() => { onEdit(service.id); setMenuOpen(false); }}>
                <IconSystem.edit size={18} />
                <span>Edit</span>
              </button>
              <button className="action-menu-item" onClick={() => { onEdit(service.id); setMenuOpen(false); }}>
                <IconSystem.copy size={18} />
                <span>Copy</span>
              </button>
              <div className="action-menu-divider" />
              <button className="action-menu-item action-menu-item-danger" onClick={() => { onDelete(service.id); setMenuOpen(false); }}>
                <IconSystem.trash size={18} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {service.description && (
        <p style={{
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          margin: 0,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {service.description}
        </p>
      )}

      {/* Details Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{service.category}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>UOM</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{service.uom}</div>
        </div>
        {primaryTax && (
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Tax</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
              {primaryTax.taxCode} - {primaryTax.taxRate}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${service.status.toLowerCase()}`}>
            {service.status}
          </span>
        </div>
      </div>

      {/* Footer */}
      {glAccount && (
        <div style={{
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          fontSize: '0.75rem',
          color: '#64748b'
        }}>
          <strong>GL Account:</strong> {glAccount.code} - {glAccount.name}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ACTION MENU COMPONENT
// ============================================================================

interface ActionMenuProps {
  rowId: string;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
  onMenuStateChange: (rowId: string, isOpen: boolean) => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  rowId,
  onView,
  onEdit,
  onCopy,
  onDelete,
  onMenuStateChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    onMenuStateChange(rowId, isOpen);
  }, [isOpen, rowId, onMenuStateChange]);

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <IconSystem.moreVertical size={16} />
      </button>

      {isOpen && (
        <div className="action-menu-dropdown">
          <button className="action-menu-item" onClick={() => { onView(rowId); setIsOpen(false); }}>
            <IconSystem.eye size={18} />
            <span>View Details</span>
          </button>
          <button className="action-menu-item" onClick={() => { onEdit(rowId); setIsOpen(false); }}>
            <IconSystem.edit size={18} />
            <span>Edit</span>
          </button>
          <button className="action-menu-item" onClick={() => { onCopy(rowId); setIsOpen(false); }}>
            <IconSystem.copy size={18} />
            <span>Copy</span>
          </button>
          <div className="action-menu-divider" />
          <button className="action-menu-item action-menu-item-danger" onClick={() => { onDelete(rowId); setIsOpen(false); }}>
            <IconSystem.trash size={18} />
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN SERVICE MASTER SCREEN COMPONENT
// ============================================================================

const SampleMasterScreen: React.FC = () => {
  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  
  // Column visibility state (v2.0 - NEW)
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    return defaultVisible;
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState<string[]>([]); // v2.0 - Changed to array for multi-select

  // Selection state
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeMenuRowId, setActiveMenuRowId] = useState<string | null>(null);

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceMasterRecord | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  // Form state
  const [formData, setFormData] = useState<SampleMasterFormData>({
    code: '',
    serviceName: '',
    description: '',
    category: '',
    entities: [],
    uom: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: '',
    status: 'Active',
    glAccount: '',
    taxDetails: [],
    scope: 'Entity'
  });

  // Filtered data
  const filteredData = useMemo(() =>
    sampleServices.filter(service => {
      const matchesSearch =
        service.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = categoryFilter === '' || service.category === categoryFilter;
      const matchesStatus = statusFilter === '' || service.status === statusFilter;
      
      // v2.0 - Multi-entity filter
      const matchesEntity = entityFilter.length === 0 || 
        service.entities.some(e => entityFilter.includes(e));
      
      return matchesSearch && matchesCategory && matchesStatus && matchesEntity;
    }),
    [searchQuery, categoryFilter, statusFilter, entityFilter]
  );

  // Get visible column definitions (v2.0 - NEW)
  const visibleColumnDefs = useMemo(() => 
    columnDefinitions.filter(col => visibleColumns.includes(col.id)),
    [visibleColumns]
  );

  // Column visibility handlers (v2.0 - NEW)
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    // Save to localStorage
    localStorage.setItem('service-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('service-master-columns');
  }, []);

  // Load saved column preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem('service-master-columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load column preferences');
      }
    }
  }, []);

  // Handlers
  const handleMenuStateChange = useCallback((rowId: string, isOpen: boolean) => {
    setActiveMenuRowId(isOpen ? rowId : null);
  }, []);

  const handleView = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    setSelectedService(service || null);
    setViewModalOpen(true);
  }, []);

  const handleEdit = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    if (service) {
      setFormData({
        code: service.code,
        serviceName: service.serviceName,
        description: service.description,
        category: service.category,
        entities: service.entities,
        uom: service.uom,
        effectiveFrom: service.effectiveFrom,
        effectiveTo: service.effectiveTo,
        status: service.status,
        glAccount: service.glAccount,
        taxDetails: service.taxDetails,
        scope: service.scope
      });
      setFormMode('edit');
      setSelectedService(service);
      setFormModalOpen(true);
    }
  }, []);

  const handleCopy = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    if (service) {
      setFormData({
        code: '',
        serviceName: service.serviceName + ' (Copy)',
        description: service.description,
        category: service.category,
        entities: service.entities,
        uom: service.uom,
        effectiveFrom: new Date().toISOString().split('T')[0],
        effectiveTo: '',
        status: 'Active',
        glAccount: service.glAccount,
        taxDetails: service.taxDetails,
        scope: service.scope
      });
      setFormMode('copy');
      setSelectedService(service);
      setFormModalOpen(true);
    }
  }, []);

  const handleDelete = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    setSelectedService(service || null);
    setDeleteModalOpen(true);
  }, []);

  const handleAddNew = useCallback(() => {
    setFormData({
      code: '',
      serviceName: '',
      description: '',
      category: '',
      entities: [],
      uom: '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
      status: 'Active',
      glAccount: '',
      taxDetails: [],
      scope: 'Entity'
    });
    setFormMode('create');
    setSelectedService(null);
    setFormModalOpen(true);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map(s => s.id));
    }
  }, [selectedRows.length, filteredData]);

  const handleSelectRow = useCallback((id: string) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  }, []);

  // v2.0 - Category change handler (with cascading defaults)
  const handleCategoryChange = useCallback((categoryName: string) => {
    setFormData(prev => {
      const category = sampleCategories.find(c => c.name === categoryName);
      if (category) {
        return {
          ...prev,
          category: categoryName,
          entities: category.defaultEntities,
          glAccount: category.defaultGLAccount,
          taxDetails: category.defaultTaxes
        };
      }
      return { ...prev, category: categoryName };
    });
  }, []);

  // v2.0 - Similar service selection handler
  const handleSelectExistingService = useCallback((service: ServiceMasterRecord) => {
    if (window.confirm(`Map existing service "${service.serviceName}" to current BU/Entity?`)) {
      console.log('Mapping existing service:', service.id);
      setFormModalOpen(false);
      // In production: API call to map service to current context
    }
  }, []);

  // v2.0 - Form save with validation
  const handleSaveForm = useCallback(() => {
    // Validation
    if (!formData.serviceName || !formData.category || !formData.uom) {
      alert('Please fill in all required fields');
      return;
    }

    // v2.0 - Single tax validation at entity level
    if (formData.scope === 'Entity' && formData.taxDetails.length > 1) {
      alert('Only one tax can be selected at Entity level');
      return;
    }

    // v2.0 - Entity→Enterprise rule
    console.log('Service created at:', formData.scope);
    console.log('Will be copied to Enterprise level for visibility');

    // In production: API call to save service
    console.log('Saving service:', formData);
    setFormModalOpen(false);
  }, [formData]);

  // Define view mode options
  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View'
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Master',
      onClick: handleAddNew,
      tooltip: 'Add New Master',
      variant: 'icon',
      visible:true
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => console.log('Refresh clicked'),
      tooltip: 'Refresh',
      variant: 'icon'
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => console.log('Export clicked'),
      tooltip: 'Export',
      variant: 'icon'
    }
  ];

  return (
    <div className="module-master-container">
      {/* Module Header - Now using ModuleHeader component */}
      <ModuleHeader
      visible={true}
        title="Sample Master Definition"
        description="Catalogue of all services offered by the enterprise"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search services...'
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode)
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen
        }}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* Module Content */}
      <div className="module-content" style={{ marginTop: '12px' }}>
        {/* Data Grid Container */}
        <div className="data-grid-container">
          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <>
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      {visibleColumnDefs.map(col => {
                        if (col.id === 'select') {
                          return (
                            <th key={col.id} style={{ width: col.width }}>
                              <input
                                type="checkbox"
                                className="row-select-all"
                                checked={selectedRows.length === filteredData.length && filteredData.length > 0}
                                onChange={handleSelectAll}
                              />
                            </th>
                          );
                        }
                        if (col.id === 'actions') {
                          return (
                            <th key={col.id} className="action-column" style={{ width: col.width }}>
                              {col.label}
                            </th>
                          );
                        }
                        return (
                          <th key={col.id} style={{ width: col.width }}>
                            {col.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumnDefs.length} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                          No services found
                        </td>
                      </tr>
                    ) : (
                      filteredData.map(service => {
                        const glAccount = sampleGLAccounts.find(gl => gl.id === service.glAccount);
                        const entitiesDisplay = service.entities.map(eid => {
                          const entity = sampleEntities.find(e => e.id === eid);
                          return entity?.name || eid;
                        }).join(', ');

                        return (
                          <tr
                            key={service.id}
                            className={activeMenuRowId === service.id ? 'has-active-menu' : ''}
                          >
                            {visibleColumnDefs.map(col => {
                              if (col.id === 'select') {
                                return (
                                  <td key={col.id}>
                                    <input
                                      type="checkbox"
                                      className="row-select"
                                      checked={selectedRows.includes(service.id)}
                                      onChange={() => handleSelectRow(service.id)}
                                    />
                                  </td>
                                );
                              }
                              if (col.id === 'code') return <td key={col.id}>{service.code}</td>;
                              if (col.id === 'serviceName') return <td key={col.id}>{service.serviceName}</td>;
                              if (col.id === 'description') return <td key={col.id}>{service.description}</td>;
                              if (col.id === 'category') return <td key={col.id}>{service.category}</td>;
                              if (col.id === 'uom') return <td key={col.id}>{service.uom}</td>;
                              if (col.id === 'status') return (
                                <td key={col.id}>
                                  <span className={`badge badge-${service.status.toLowerCase()}`}>
                                    {service.status}
                                  </span>
                                </td>
                              );
                              if (col.id === 'entities') return <td key={col.id}>{entitiesDisplay}</td>;
                              if (col.id === 'effectiveFrom') return <td key={col.id}>{service.effectiveFrom}</td>;
                              if (col.id === 'effectiveTo') return <td key={col.id}>{service.effectiveTo || '-'}</td>;
                              if (col.id === 'glAccount') return (
                                <td key={col.id}>{glAccount ? `${glAccount.code} - ${glAccount.name}` : '-'}</td>
                              );
                              if (col.id === 'actions') return (
                                <td key={col.id} className="action-column">
                                  <ActionMenu
                                    rowId={service.id}
                                    onView={handleView}
                                    onEdit={handleEdit}
                                    onCopy={handleCopy}
                                    onDelete={handleDelete}
                                    onMenuStateChange={handleMenuStateChange}
                                  />
                                </td>
                              );
                              return null;
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-footer">
                <div className="pagination-info">
                  Showing {filteredData.length} of {sampleServices.length} services
                </div>
                <div className="pagination-controls">
                  <button className="page-btn">
                    <IconSystem.chevronLeft size={16} />
                  </button>
                  <button className="page-btn active">1</button>
                  <button className="page-btn">
                    <IconSystem.chevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* v2.0 - GRID VIEW */}
          {viewMode === 'grid' && (
            <div style={{
              padding: '1rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '1.5rem',
              overflowY: 'auto'
            }}>
              {filteredData.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>No services found</p>
                </div>
              ) : (
                filteredData.map(service => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          )}

          {/* v2.0 - CALENDAR VIEW (DISABLED MESSAGE) */}
          {viewMode === 'calendar' && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Service Master</p>
            </div>
          )}
        </div>

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
          </div>

          <div className="search-panel-content">
            {/* Category Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Category</label>
              <div className="form-group">
                {['', ...sampleCategories.map(c => c.name)].map((cat) => (
                  <label key={cat} className="checkbox-label">
                    <input
                      type="radio"
                      name="category"
                      checked={categoryFilter === cat}
                      onChange={() => setCategoryFilter(cat)}
                    />
                    {cat || 'All Categories'}
                  </label>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Status</label>
              <div className="form-group">
                {['', 'Active', 'Inactive'].map((status) => (
                  <label key={status} className="checkbox-label">
                    <input
                      type="radio"
                      name="status"
                      checked={statusFilter === status}
                      onChange={() => setStatusFilter(status)}
                    />
                    {status || 'All'}
                  </label>
                ))}
              </div>
            </div>

            {/* v2.0 - Multi-select BU/Entity Filter */}
            <div className="filter-section">
              <label className="filter-section-title">BU / Entity</label>
              <MultiSelectDropdown
                options={sampleEntities}
                selectedIds={entityFilter}
                onChange={setEntityFilter}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setCategoryFilter('');
                setStatusFilter('');
                setEntityFilter([]);
              }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setFilterOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* SERVICE FORM MODAL */}
      <SampleMasterFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
        categories={sampleCategories}
        entities={sampleEntities}
        glAccounts={sampleGLAccounts}
        uoms={sampleUOMs}
        taxServices={sampleTaxServices}
        existingServices={sampleServices}
        onCategoryChange={handleCategoryChange}
        onSelectExistingService={handleSelectExistingService}
        openedBy="SampleMasterScreen"
      />

      {/* VIEW MODAL */}
      <ServiceMasterViewModal
        isOpen={viewModalOpen}
        service={selectedService}
        onClose={() => setViewModalOpen(false)}
        onEdit={(service) => handleEdit(service.id)}
        glAccounts={sampleGLAccounts}
        entities={sampleEntities}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <ServiceMasterDeleteModal
        isOpen={deleteModalOpen}
        service={selectedService}
        onConfirm={() => {
          if (selectedService) {
            console.log('Delete:', selectedService.id);
            // In production: API call to delete service
          }
          setSelectedService(null);
        }}
        onClose={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default SampleMasterScreen;